<script setup lang="ts">
  import { useSipStore } from '@bv/stores';
  import { sip, trans } from '@bv/utils';
  import { storeToRefs } from 'pinia';
  import { ref, computed, watch, onBeforeUnmount, onBeforeMount } from 'vue';
  import { useRoute } from 'vue-router';

  import VoipModal from '@/components/SharedComponents/Dialog/VoipModal.vue';
  import { livePanelStore } from '@/stores/socket/live-panel-socket.store';

  import AvailableOptions from './AvailableOptions/AvailableOptions.vue';

  const selectedValue = ref<Partial<{ value: string }> | null>(null);
  const isTransferring = ref<boolean>(false);

  const panelStore = livePanelStore();
  const sipStore = useSipStore();
  const route = useRoute();

  const { availableExtensions, queuesArray } = storeToRefs(panelStore);
  const { activeCall } = storeToRefs(sipStore);

  const { startWebPhoneReactive, stopReactive } = panelStore;

  const emit = defineEmits(['on-close']);

  const allOptions = computed(() => ({
    extensions: {
      data: availableExtensions.value.map((extension: any) => ({
        name: extension.name === extension.number ? extension.name : `${extension.name} (${extension.number})`,
        value: extension.number,
      })),
      name: trans('call_center.webphone.extensions'),
    },
    queues: {
      data: queuesArray.value.map((queue: any) => ({
        name: queue.name === queue.number ? queue.name : `${queue.name} (${queue.number})`,
        value: queue.number,
      })),
      name: trans('call_center.webphone.queues'),
    },
  }));

  const closeDialog = (): void => {
    emit('on-close');
  };

  const select = (value: Partial<{ value: string }> | null): void => {
    selectedValue.value = value;
  };

  const transferCall = (): void => {
    if (activeCall.value && selectedValue.value?.value) {
      isTransferring.value = true;
      sip
        .transfer(activeCall.value.id, selectedValue.value.value)
        .then(() => {
          closeDialog();
        })
        .finally(() => {
          isTransferring.value = false;
        });
    }
  };

  const resetSelectedValue = (): void => {
    selectedValue.value = null;
  };

  const checkActiveCall = (): void => {
    if (activeCall.value?.id !== route.query.transfer) {
      closeDialog();
    }
  };

  watch(activeCall, checkActiveCall, { deep: true });

  onBeforeMount(() => {
    checkActiveCall();
    startWebPhoneReactive();
  });

  onBeforeUnmount(() => {
    stopReactive();
  });
</script>

<template>
  <voip-modal
    class="transfer-dialog"
    :title="$t('call_center.webphone.transfer_message')"
    @closeDialog="closeDialog"
  >
    <template #content>
      <available-options
        :options="allOptions"
        @onSearch="resetSelectedValue"
        @onSelect="select"
      />
    </template>

    <template #footer>
      <bv-btn
        color="var(--color-green-500)"
        :disabled="!selectedValue?.value || isTransferring"
        @click="transferCall"
      >
        {{ $t('call_center.webphone.transfer_call') }}
      </bv-btn>
    </template>
  </voip-modal>
</template>

<style lang="scss" scoped>
  .button {
    padding: 16px;
    border-radius: var(--mainRadius);
    background: var(--green500);
    color: var(--shadesWhite);
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 14px;

    &:disabled {
      cursor: not-allowed;
    }
  }

  .inputs-dialog.transfer-dialog {
    :deep {
      flex-direction: column;
      padding-top: 44px;

      .inputs-dialog__parent {
        max-height: calc(100% - 16px);
        flex: 1;
        overflow-y: hidden;
        display: flex;
        flex-direction: column;

        .inputs-dialog__parent__content-parent {
          display: flex;
          flex-direction: column;
          flex: 1;
          overflow-y: hidden;
          padding: 16px 8px;

          & > div {
            display: flex;
            flex-direction: column;
            flex: 1;
            overflow-y: hidden;
          }

          .inputs-dialog__parent__content-parent__content {
            display: flex;
            flex-direction: column;
            flex: 1;
            overflow-y: hidden;
          }
        }
      }
    }
  }
</style>
